/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable no-param-reassign */
/* eslint-disable @typescript-eslint/no-non-null-assertion */
import React, {
  useEffect, MutableRefObject, useRef, useCallback,
} from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { fabric } from 'fabric';

interface ICanvasProps {
  canvasRef: MutableRefObject<HTMLCanvasElement | null>;
  // eslint-disable-next-line
  mapImageImage: { data: any; loading: boolean };
  imageLoaded: boolean;
  imageTransparency: number;
  canvas: fabric.Canvas | null;
  setCanvas: React.Dispatch<React.SetStateAction<fabric.Canvas | null>>;
  setImageDimensions: React.Dispatch<React.SetStateAction<{
    width: number; height: number;
  } | null>>;
  handleCanvasClick: (e: fabric.IEvent) => void;
  undoLastPoint: () => void;
  snapToGrid: boolean;
  gridStep: number;
}

const Canvas: React.FC<ICanvasProps> = ({
  canvasRef,
  mapImageImage,
  imageLoaded,
  imageTransparency,
  canvas,
  setCanvas,
  setImageDimensions,
  handleCanvasClick,
  undoLastPoint,
  snapToGrid,
  gridStep,
}) => {
  const canvasContainerRef = useRef<HTMLDivElement>(null);
  const gridGroupRef = useRef<fabric.Group | null>(null);

  const drawGrid = useCallback((fabricCanvas: fabric.Canvas) => {
  // Remove old grid
    if (gridGroupRef.current) {
      fabricCanvas.remove(gridGroupRef.current);
      gridGroupRef.current = null;
    }

    if (!snapToGrid) {
      fabricCanvas.renderAll();
      return;
    }

    const width = fabricCanvas.width || 0;
    const height = fabricCanvas.height || 0;
    const lines: fabric.Line[] = [];

    // Create all lines
    for (let i = 0; i <= width; i += gridStep) {
      lines.push(new fabric.Line([i, 0, i, height], {
        stroke: 'rgba(200, 200, 200, 0.3)',
        strokeWidth: 1,
        selectable: false,
        evented: false,
      }));
    }

    for (let i = 0; i <= height; i += gridStep) {
      lines.push(new fabric.Line([0, i, width, i], {
        stroke: 'rgba(200, 200, 200, 0.3)',
        strokeWidth: 1,
        selectable: false,
        evented: false,
      }));
    }

    // Group all lines together for better performance
    const gridGroup = new fabric.Group(lines, {
      selectable: false,
      evented: false,
      objectCaching: true,
    });

    gridGroupRef.current = gridGroup;
    fabricCanvas.add(gridGroup);
    fabricCanvas.sendToBack(gridGroup);
    fabricCanvas.renderAll();
  }, [snapToGrid, gridStep]);

  // Draw grid when settings change:
  useEffect(() => {
    if (canvas) {
      drawGrid(canvas);
      canvas.renderAll();
    }
  }, [canvas, snapToGrid, gridStep, drawGrid]);

  // Put canvas over image and initialize fabric
  useEffect(() => {
    if (imageLoaded && canvasRef.current && !canvas) {
      const img = new Image();
      img.onload = () => {
        const containerWidth = canvasContainerRef.current?.clientWidth || 800;

        setImageDimensions({
          width: img.width,
          height: img.height,
        });

        const scale = containerWidth / img.width;
        const height = img.height * scale;

        const parentContainer = canvasContainerRef.current?.parentElement;
        if (parentContainer) {
          parentContainer.style.height = `${height}px`;
        }

        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        const fabricCanvas = new fabric.Canvas(canvasRef.current!, {
          width: containerWidth,
          height,
          selection: false,
          backgroundColor: 'transparent',
        });

        fabric.Image.fromURL(URL.createObjectURL(mapImageImage.data as Blob), (fabricImage) => {
          const imgScale = containerWidth / fabricImage.width!;
          fabricImage.set({
            scaleX: imgScale,
            scaleY: imgScale,
            selectable: false,
            evented: false,
            opacity: 1 - imageTransparency,
            stroke: '#000000',
            strokeWidth: 1,
          });
          fabricCanvas.setBackgroundImage(fabricImage, fabricCanvas.renderAll.bind(fabricCanvas));
        });

        const wrapper = canvasRef.current?.parentElement;
        if (wrapper) {
          wrapper.style.position = 'relative';
          wrapper.style.width = '100%';
          wrapper.style.height = '100%';
        }

        setCanvas(fabricCanvas);
      };
      img.src = URL.createObjectURL(mapImageImage.data as Blob);
    }
  }, [imageLoaded, mapImageImage.data, canvas]);

  useEffect(() => () => {
    if (canvas) {
      canvas.dispose();
    }
  }, []);

  // Handle canvas clicks for drawing
  useEffect(() => {
    if (!canvas) return;

    canvas.on('mouse:down', handleCanvasClick);

    // eslint-disable-next-line consistent-return
    return () => {
      canvas.off('mouse:down', handleCanvasClick);
    };
  }, [canvas, handleCanvasClick]);

  // Undo last change listener
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        undoLastPoint();
      }
    };

    window.addEventListener('keydown', handleKeyDown);

    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [undoLastPoint]);

  useEffect(() => {
    if (canvas?.backgroundImage && canvas.backgroundImage instanceof fabric.Image) {
      canvas.backgroundImage.set('opacity', 1 - imageTransparency);
      canvas.requestRenderAll();
    }
  }, [canvas, imageTransparency]);

  return (
    <div ref={canvasContainerRef} className="zone-maker-canvas-wrapper">
      <canvas ref={canvasRef} />
    </div>
  );
};

export default Canvas;
